import { call, put, takeLatest, takeEvery, cancel } from 'redux-saga/effects';
import { change, initialize } from 'redux-form';
import moment from 'moment';

import * as action from '../../actions';
import { setDashboard, setOutlets } from '../../actions/dashboard';
import {
  GET_DASHBOARD_REQUEST,
  DASHBOARD_CHANGE_OUTLET,
  DASHBOARD_PAGE_REQUEST,
  DASHBOARD_CHANGE_DATE,
  DASHBOARD_ITEM_SUMMARY_REQUEST,
  DASHBOARD_TOP_ITEMS_BY_CATEGORY_REQUEST,
  DASHBOARD_TOP_CATEGORIES_BY,
  DASHBOARD_TABLE_SUMMARY_REQUEST,
  DASHBOARD_LAZY_LOAD_PAGE_REQUEST,
} from '../../actions/dashboard/constants';
import { LOGOUT } from '../../actions/constants';
import * as actNotification from '../../actions/notifications';
import * as actActionBar from '../../actions/actionBar';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';

import * as authDashboard from '../../api/dashboard';
import * as apiOutlet from '../../api/outlets';
import {
  getFormattedEndDate,
  DEFAULT_START_HOUR,
  DEFAULT_END_HOUR,
} from '../../utils/hourFilter';
import {
  setSelectedOutletIdToLocalStorage,
  getSelectedOutletIdFromLocalStorage,
} from '../../utils/selectedOutletId';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapDashboard from '../../clevertap/dashboard';
import { getFilteredDateAsMoment } from '../api/get-filtered-date';

function* retrieveDashboardSalesSummaryData(query, outletIds) {
  const dashboardSummary = yield call(authDashboard.getDashboardSalesSummary, {
    ...query,
    outletIds,
  });
  return dashboardSummary;
}

function* retrieveDashboardItemSummaryData(query, outletIds) {
  const dashboardSummary = yield call(authDashboard.getDashboardTopItems, {
    ...query,
    outletIds,
  });
  return dashboardSummary;
}

export function* fillFormSearch(outletIds, startDate, endDate) {
  // only all day that will give 23:59:59. hour filter is hour based
  const allDay = moment(endDate).format('HH:mm') === '23:59';
  const startHour = allDay
    ? DEFAULT_START_HOUR
    : moment(startDate).format('HH:mm');
  const endHour = allDay ? DEFAULT_END_HOUR : moment(endDate).format('HH:mm');
  // init form value
  yield put(
    initialize('search', {
      outlet: outletIds,
      date: { startDate, endDate, startHour, endHour, allDay },
    })
  );
}

export function* pageRequest() {
  yield put(
    setDashboard({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      gross_sales: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      net_sales: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_transactions: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      top_items: [],
      transactions: [],
      dailyGrossSales: [],
      weeklyGrossSales: [],
      hourlyGrossSales: [],
      categorySalesAmountByRevenue: {},
      categorySalesAmountByVolume: {},
      topItemsByCategory: {},
      tableManagementReport: {},
    })
  );
  try {
    yield put(action.showProgressBar());
    yield put(showPageLoadingSpinner(true));
    const data = yield call(getFilteredDateAsMoment);
    const { startDate, endDate } = data;
    const formattedStart = moment(startDate).format('YYYY-MM-DD HH:mm:ss');
    const formattedEnd = moment(endDate).format('YYYY-MM-DD HH:mm:ss');

    const resOutlet = yield call(apiOutlet.outlets);
    yield put(setOutlets(resOutlet.data));
    yield put(actActionBar.setOutlets(resOutlet.data));

    const outletIds = getSelectedOutletIdFromLocalStorage();
    yield put(actActionBar.setSelectedOutletIds(outletIds));

    yield call(
      fillFormSearch,
      getSelectedOutletIdFromLocalStorage(),
      formattedStart,
      formattedEnd
    );
    // call dashboard api
    const query = {
      startDate: formattedStart,
      endDate: getFormattedEndDate(formattedEnd),
    };

    let tableManagementReport = {};
    try {
      tableManagementReport = yield call(authDashboard.getTableDashboard, {
        startDate: startDate.format(),
        endDate: endDate.format(),
        outletIds: outletIds.join(),
      });
    } catch (error) {
      yield put(actNotification.error('Failed to load data'));
    }

    try {
      const salesSummaryReport = yield retrieveDashboardSalesSummaryData(
        query,
        outletIds
      );
      const itemSummaryReport = yield retrieveDashboardItemSummaryData(
        query,
        outletIds
      );

      let categorySalesAmountBy = {};
      let topItemsByCategory = {};

      categorySalesAmountBy = yield call(
        authDashboard.getDashboardTopCategoriesBy,
        {
          ...query,
          outletIds,
        }
      );
      topItemsByCategory = yield call(
        authDashboard.getDashboardTopItemsByCategory,
        {
          ...query,
          outletIds,
        }
      );

      yield put(
        setDashboard({
          ...salesSummaryReport,
          ...itemSummaryReport,
          ...categorySalesAmountBy,
          ...topItemsByCategory,
          tableManagementReport: (tableManagementReport as any).data,
        })
      );
      yield put(showPageLoadingSpinner(false));
    } catch (error) {
      yield put(showPageLoadingSpinner(false));
      yield put(actNotification.error('Failed to load data'));
    } finally {
      yield put(action.hideProgressBar());
    }
    clevertapDashboard.dashboardStart({
      outletIds,
      startDate,
      endDate,
    });
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotification.error(error.message));
  } finally {
    yield put(action.hideProgressBar());
  }
}

export function* lazyLoadPageRequest() {
  yield put(
    setDashboard({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      gross_sales: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      net_sales: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_transactions: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      top_items: [],
      transactions: [],
      dailyGrossSales: [],
      weeklyGrossSales: [],
      hourlyGrossSales: [],
      categorySalesAmountByRevenue: {},
      categorySalesAmountByVolume: {},
      topItemsByCategory: {},
      tableManagementReport: {},
    })
  );
  try {
    yield put(action.showProgressBar());
    yield put(showPageLoadingSpinner(true));

    const data = yield call(getFilteredDateAsMoment);
    const { startDate, endDate } = data;
    const formattedStart = moment(startDate).format('YYYY-MM-DD HH:mm:ss');
    const formattedEnd = moment(endDate).format('YYYY-MM-DD HH:mm:ss');

    const resOutlet = yield call(apiOutlet.outlets);
    yield put(setOutlets(resOutlet.data));
    yield put(actActionBar.setOutlets(resOutlet.data));

    const outletIds = getSelectedOutletIdFromLocalStorage();
    yield put(actActionBar.setSelectedOutletIds(outletIds));

    yield call(fillFormSearch, outletIds, formattedStart, formattedEnd);
    // call dashboard api
    const query = {
      startDate: formattedStart,
      endDate: getFormattedEndDate(formattedEnd),
    };

    try {
      const dashboardSummary = yield retrieveDashboardSalesSummaryData(
        query,
        outletIds
      );

      yield put(
        setDashboard({
          ...dashboardSummary,
        })
      );
      yield put(showPageLoadingSpinner(false));
    } catch (error) {
      yield put(showPageLoadingSpinner(false));
      yield put(actNotification.error('Failed to load data'));
    } finally {
      yield put(action.hideProgressBar());
    }
    clevertapDashboard.dashboardStart({
      outletIds,
      startDate,
      endDate,
    });
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotification.error(error.message));
  } finally {
    yield put(action.hideProgressBar());
  }
}

export function* getDashboardRequestFlow(request) {
  yield put(showPageLoadingSpinner(true));
  const { payload } = request;
  let { query } = payload;
  if (!query) {
    // kalo gak ada startdate sama enddate, panggil api calendar untuk mengetahui current date
    const data = yield call(getFilteredDateAsMoment);
    const { startDate, endDate } = data;
    yield put(change('search', 'date', { startDate, endDate }));
    query = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      start_date: startDate.format('D/M/YYYY'),
      // eslint-disable-next-line @typescript-eslint/naming-convention
      end_date: endDate.format('D/M/YYYY'),
    };
  }

  yield put(action.sendingRequest(true));
  yield put(action.showProgressBar());

  const resOutlet = yield call(apiOutlet.outlets);
  yield put(setOutlets(resOutlet.data));
  try {
    yield put(
      change('search', 'outlet', getSelectedOutletIdFromLocalStorage())
    );
    const dashboardData = yield call(authDashboard.dashboard, query);
    yield put(setDashboard(dashboardData.data));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotification.error(error.message));
  } finally {
    yield put(action.sendingRequest(false));
    yield put(action.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* changeOutletFlow(request) {
  yield put(
    setDashboard({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      gross_sales: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      net_sales: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_transactions: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      top_items: [],
      transactions: [],
      dailyGrossSales: [],
      weeklyGrossSales: [],
      hourlyGrossSales: [],
      categorySalesAmountByRevenue: {},
      categorySalesAmountByVolume: {},
      topItemsByCategory: {},
      tableManagementReport: {},
    })
  );
  const { payload } = request;
  const { outletIds, startDate, endDate } = payload;
  yield put(showPageLoadingSpinner(true));
  setSelectedOutletIdToLocalStorage(outletIds);
  yield put(change('search', 'date', { startDate, endDate }));
  yield put(change('search', 'outlet', outletIds));
  yield put(actActionBar.setSelectedOutletIds(outletIds));

  try {
    const query = { startDate, endDate: getFormattedEndDate(endDate) };
    const dashboardSummary = yield retrieveDashboardSalesSummaryData(
      query,
      outletIds
    );

    yield put(
      setDashboard({
        ...dashboardSummary,
      })
    );
    yield call(fillFormSearch, outletIds, startDate, endDate);
    yield put(showPageLoadingSpinner(false));
  } catch (error) {
    yield put(showPageLoadingSpinner(false));
    yield put(actNotification.error('Failed to load data'));
  } finally {
    yield put(action.hideProgressBar());
  }
}

export function* changeDateFlow(request) {
  yield put(
    setDashboard({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      gross_sales: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      net_sales: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      total_transactions: 0,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      top_items: [],
      transactions: [],
      dailyGrossSales: [],
      weeklyGrossSales: [],
      hourlyGrossSales: [],
      categorySalesAmountByRevenue: {},
      categorySalesAmountByVolume: {},
      topItemsByCategory: {},
      tableManagementReport: {},
    })
  );
  try {
    yield put(showPageLoadingSpinner(true));
    const {
      payload: { startDate, endDate, outlet },
    } = request;
    const query = { startDate, endDate: getFormattedEndDate(endDate) };
    const dashboardSummary = yield retrieveDashboardSalesSummaryData(
      query,
      outlet
    );

    yield put(
      setDashboard({
        ...dashboardSummary,
      })
    );
    yield call(fillFormSearch, outlet, startDate, endDate);
    yield put(showPageLoadingSpinner(false));
  } catch (error) {
    yield put(showPageLoadingSpinner(false));
    yield put(actNotification.error('Failed to load data'));
  } finally {
    yield put(action.hideProgressBar());
  }
}

export function* getItemSummaryRequest(request) {
  let itemSummaryReport = {};
  const {
    payload: { startDate, endDate, outlet },
  } = request;
  const query = { startDate, endDate: getFormattedEndDate(endDate) };
  try {
    itemSummaryReport = yield retrieveDashboardItemSummaryData(query, outlet);
  } catch (error) {
    yield put(actNotification.error('Failed to load data'));
  }
  yield put(
    setDashboard({
      ...itemSummaryReport,
    })
  );
}

export function* getTopCategoriesByRequest(request) {
  let categorySalesAmountBy = {};
  const {
    payload: { startDate, endDate, outlet },
  } = request;
  const query = { startDate, endDate: getFormattedEndDate(endDate) };
  try {
    categorySalesAmountBy = yield call(
      authDashboard.getDashboardTopCategoriesBy,
      {
        ...query,
        outletIds: outlet,
      }
    );
  } catch (error) {
    yield put(actNotification.error('Failed to load data'));
  }
  yield put(
    setDashboard({
      ...categorySalesAmountBy,
    })
  );
}

export function* getTopItemsByCategoryRequest(request) {
  let topItemsByCategory = {};
  const {
    payload: { startDate, endDate, outlet },
  } = request;
  const query = { startDate, endDate: getFormattedEndDate(endDate) };
  try {
    topItemsByCategory = yield call(
      authDashboard.getDashboardTopItemsByCategory,
      {
        ...query,
        outletIds: outlet,
      }
    );
  } catch (error) {
    yield put(actNotification.error('Failed to load data'));
  }
  yield put(
    setDashboard({
      ...topItemsByCategory,
    })
  );
}

export function* tableSummaryRequest(request) {
  let tableManagementReport = {};
  const {
    payload: { startDate, endDate, outlet },
  } = request;
  try {
    const queryParams = {
      startDate: moment(startDate).format(),
      endDate: moment(endDate).format(),
      outletIds: outlet.join(),
    };
    tableManagementReport = yield call(
      authDashboard.getTableDashboard,
      queryParams
    );
  } catch (error) {
    yield put(actNotification.error('Failed to load data'));
  }
  yield put(
    setDashboard({
      tableManagementReport: (tableManagementReport as any).data,
    })
  );
}

export function* cancelAction(args) {
  yield cancel(...args);
}

export default function* dashboardSaga() {
  const pageReqTask = yield takeLatest(DASHBOARD_PAGE_REQUEST, pageRequest);
  const lazyLoadPageReqTask = yield takeLatest(
    DASHBOARD_LAZY_LOAD_PAGE_REQUEST,
    lazyLoadPageRequest
  );
  const task = yield takeLatest(GET_DASHBOARD_REQUEST, getDashboardRequestFlow);
  const task2 = yield takeLatest(DASHBOARD_CHANGE_OUTLET, changeOutletFlow);
  const changeDateTask = yield takeLatest(
    DASHBOARD_CHANGE_DATE,
    changeDateFlow
  );
  const itemSummaryTask = yield takeLatest(
    DASHBOARD_ITEM_SUMMARY_REQUEST,
    getItemSummaryRequest
  );

  const topItemsByCategoryTask = yield takeLatest(
    DASHBOARD_TOP_ITEMS_BY_CATEGORY_REQUEST,
    getTopItemsByCategoryRequest
  );

  const topCategoriesByTask = yield takeLatest(
    DASHBOARD_TOP_CATEGORIES_BY,
    getTopCategoriesByRequest
  );

  const tableSummaryTask = yield takeLatest(
    DASHBOARD_TABLE_SUMMARY_REQUEST,
    tableSummaryRequest
  );

  yield takeEvery(LOGOUT, cancelAction, [
    task,
    task2,
    pageReqTask,
    lazyLoadPageReqTask,
    changeDateTask,
    itemSummaryTask,
    topItemsByCategoryTask,
    topCategoriesByTask,
    tableSummaryTask,
  ]);
}
